import {module as Module} from "modujs";
import {whenReady} from "../utils/fonts";
import {FONT} from "../config";
import {gsap} from "gsap";
import {isTouchDevice} from "../utils/is";
import {createFocusTrap} from "focus-trap";
import {$html} from "../utils/dom";
import {loadImage} from "../utils/image";
import {$screen} from "../stores/screen.js";

export default class Lightbox extends Module {
    static ZOOM = 2;
    static LERP = 0.15;
    static MARGIN_DESKTOP = 16;
    static MARGIN_MOBILE = 10;

    constructor(m) {
        super(m);

        /* Callback */
        this.onResizeBind = this.onResize.bind(this);
        this.onToggleZoomBind = this.onToggleZoom.bind(this);
        this.onMouseMoveBind = this.onMouseMove.bind(this);
        this.onWheelBind = this.onWheel.bind(this);
        this.onUpdateBind = this.onUpdate.bind(this);
        this.onActivateBind = this.onActivate.bind(this);
        this.onPostActivateBind = this.onPostActivate.bind(this);
        this.onDeactivateBind = this.onDeactivate.bind(this);
        this.onPostDeactivateBind = this.onPostDeactivate.bind(this);
        this.onToggleLightboxBind = this.onToggleLightbox.bind(this);
        this.onCloseBind = this.onClose.bind(this);

        /* UI */
        this.$area = this.$("area")[0];
        this.$root = this.$("root")[0];
        this.$element = this.$("element")[0];
        this.$triggers = document.querySelectorAll("[data-lightbox-expand]");
        this.$close = this.$("close")[0];

        // Boolean
        this.isPlaying = false;
        this.isZoomed = false;
        this.isTouchDevice = isTouchDevice();

        /* Data */
        this.image = null;

        this.transformX = 0;
        this.targetTransformX = 0;
        this.transformY = 0;
        this.targetTransformY = 0;
        this.amplitudeX = 0;
        this.amplitudeY = 0;

        this.zoom = 1 / Lightbox.ZOOM;
        this.targetZoom = 1 / Lightbox.ZOOM;

        // Focus trap
        this.focusTrap = createFocusTrap(this.$root, {
            onActivate: this.onActivateBind,
            onPostActivate: this.onPostActivateBind,
            onDeactivate: this.onDeactivateBind,
            onPostDeactivate: this.onPostDeactivateBind,
            clickOutsideDeactivates: true,
        });
    }

    ///////////////
    // Lifecycle
    ///////////////
    init() {
        this.bindEvents();
        whenReady(FONT.EAGER).then((fonts) => this.onFontsLoaded(fonts));
    }

    destroy() {
        this.unbindEvents();
        this.pause();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        this.unbindScreenListener = $screen.subscribe(this.onResizeBind)

        for (const $trigger of this.$triggers) {
            $trigger.addEventListener("click", this.onToggleLightboxBind);
        }

        this.$close.addEventListener("click", this.onCloseBind);
    }

    unbindEvents() {
        this.unbindScreenListener?.()

        for (const $trigger of this.$triggers) {
            $trigger.removeEventListener("click", this.onToggleLightboxBind);
        }

        this.$close.removeEventListener("click", this.onCloseBind);
    }

    ///////////////
    // Callback
    ///////////////
    onResize() {
        this.resize();
    }

    onFontsLoaded() {
        this.resize();
    }

    onToggleZoom() {
        if (!this.isZoomed) {
            this.zoomIn();
        } else {
            this.zoomOut();
        }
    }

    onMouseMove(e) {
        // calculate the x and y coordinates of the mouse position relative to the element
        const x = e.clientX;
        const y = e.clientY;

        this.targetTransformX = gsap.utils.clamp(
            -this.amplitudeX,
            this.amplitudeX,
            gsap.utils.mapRange(
                this.margin,
                this.wWidth - this.margin,
                this.amplitudeX,
                -this.amplitudeX,
                x
            )
        );

        this.targetTransformY = gsap.utils.clamp(
            -this.amplitudeY,
            this.amplitudeY,
            gsap.utils.mapRange(
                this.margin,
                this.wHeight - this.margin,
                this.amplitudeY,
                -this.amplitudeY,
                y
            )
        );
    }

    onWheel(e) {
        e.preventDefault();

        if (this.isZoomed && e.deltaY > 0) {
            this.zoomOut();
        }
    }

    onUpdate() {
        this.transformX +=
            (this.targetTransformX - this.transformX) * Lightbox.LERP;
        this.transformX = ((100 * (this.transformX + 0.01)) | 0) / 100;
        this.transformY +=
            (this.targetTransformY - this.transformY) * Lightbox.LERP;
        this.transformY = ((100 * (this.transformY + 0.01)) | 0) / 100;

        this.zoom += (this.targetZoom - this.zoom) * Lightbox.LERP;
        this.zoom = ((10000 * (this.zoom + 0.0001)) | 0) / 10000;

        this.$element.style.transform = `translate3d(-50%, -50%, 0) translate3d(${this.transformX}px, ${this.transformY}px, 0) scale(${this.zoom})`;
    }

    onActivate() {
        this.el.classList.add("is-active");

        requestAnimationFrame(async () => {
            this.el.classList.add("is-ready");
            $html.classList.add("has-lightbox-open");
            this.el.setAttribute("aria-hidden", false);

            if (!this.src) return;

            this.el.classList.add("is-loading");

            this.image = await loadImage(this.src);

            /* Fit image to container */
            this.fitImageToContainer();

            /* Set image */
            this.$element.innerHTML = "";
            this.$element.appendChild(this.image.element);

            /* Set events */
            if (!this.isTouchDevice) {
                this.$element.addEventListener("click", this.onToggleZoomBind);
                window.addEventListener("mousemove", this.onMouseMoveBind);
                window.addEventListener("wheel", this.onWheelBind, {passive: false});
            }

            setTimeout(() => {
                this.el.classList.remove("is-loading");
                this.el.classList.add("is-loaded");
            }, 100);
        });
    }

    onPostActivate() {
        this.isOpen = true;
        this.play();
    }

    onDeactivate() {
        this.pause()
        this.el.classList.remove("is-ready");

        requestAnimationFrame(() => {
            this.el.classList.remove("is-active");
            $html.classList.remove("has-lightbox-open");
            this.el.setAttribute("aria-hidden", true);

            this.zoomOut();

            /*Events*/
            if (!this.isTouchDevice) {
                this.$element.removeEventListener("click", this.onToggleZoomBind);
                window.removeEventListener("mousemove", this.onMouseMoveBind);
                window.removeEventListener("wheel", this.onWheelBind, {
                    passive: false,
                });
            }

            /* Reset */
            this.transformX = 0;
            this.targetTransformX = 0;
            this.transformY = 0;
            this.targetTransformY = 0;
            this.zoom = 1 / Lightbox.ZOOM;
            this.targetZoom = 1 / Lightbox.ZOOM;
        });
    }

    onPostDeactivate() {
        this.isOpen = false;

        this.image = null;
        this.$element.innerHTML = "";
        this.$element.style = "";
        this.el.classList.remove("is-loaded");
        this.el.classList.remove("is-showing");

        this.zoomOut();
    }

    onToggleLightbox(e) {
        this.src = e.target.dataset.src;
        this.toggleLightbox();
    }

    onClose() {
        this.closeLightbox();
    }

    ///////////////
    // Methods
    ///////////////
    resize() {
        /* Window */
        this.wWidth = window.innerWidth;
        this.wHeight = window.innerHeight;

        // Compute margin
        let margin = Lightbox.MARGIN_DESKTOP;
        if (window.innerWidth < 768) {
            margin = Lightbox.MARGIN_MOBILE;
        }
        this.margin = margin;

        /* Fit image to container */
        this.image && this.fitImageToContainer();
    }

    play() {
        // Start the animation
        if (this.isPlaying) return;
        this.isPlaying = true;
        gsap.ticker.add(this.onUpdateBind);
    }

    pause() {
        // Pause the animation
        if (!this.isPlaying) return;
        this.isPlaying = false;
        gsap.ticker.remove(this.onUpdateBind);
    }

    toggleLightbox() {
        if (this.el.classList.contains("is-active")) {
            this.closeLightbox();
        } else {
            this.openLightbox();
        }
    }

    openLightbox() {
        if (this.isOpen) return;

        this.el.classList.add("is-showing");
        
        requestAnimationFrame(() => { 
            this.focusTrap?.activate?.();
        })
    }

    closeLightbox() {
        if (!this.isOpen) return;

        this.focusTrap?.deactivate?.();
    }

    zoomIn() {
        this.isZoomed = true;
        this.targetZoom = 1;
        this.amplitudeX =
            this.$area.offsetWidth - this.elementWidth * Lightbox.ZOOM < 0
                ? Math.abs(
                this.$area.offsetWidth - this.elementWidth * Lightbox.ZOOM
            ) / 2
                : 0;
        this.amplitudeY =
            this.$area.offsetHeight - this.elementHeight * Lightbox.ZOOM < 0
                ? Math.abs(
                this.$area.offsetHeight -
                this.elementHeight * Lightbox.ZOOM
            ) / 2
                : 0;

        this.el.classList.add("is-zoomed");
    }

    zoomOut() {
        this.isZoomed = false;
        this.targetZoom = 1 / Lightbox.ZOOM;
        this.amplitudeX = 0;
        this.amplitudeY = 0;
        this.targetTransformX = 0;
        this.targetTransformY = 0;

        this.el.classList.remove("is-zoomed");
    }

    fitImageToContainer() {
        // get image dimensions
        const imgWidth = this.image.width;
        const imgHeight = this.image.height;

        // get container dimensions
        const containerWidth = this.$area.offsetWidth;
        const containerHeight = this.$area.offsetHeight;

        // calculate aspect ratios
        const imgRatio = imgWidth / imgHeight;
        const containerRatio = containerWidth / containerHeight;

        // if image ratio is less than container ratio, set image width based on container height, else set image height based on container width
        if (imgRatio < containerRatio) {
            const newWidth = Math.floor(containerHeight * imgRatio);
            this.elementWidth = newWidth;
            this.elementHeight = containerHeight;
        } else {
            const newHeight = Math.floor(containerWidth / imgRatio);
            this.elementWidth = containerWidth;
            this.elementHeight = newHeight;
        }

        this.$element.style.width = this.elementWidth * Lightbox.ZOOM + "px";
        this.$element.style.height = this.elementHeight * Lightbox.ZOOM + "px";
    }
}